import {ApplicationRef, ComponentRef, inject, Injectable, Injector} from '@angular/core';
import {ComponentPortal, DomPortalOutlet, PortalOutlet} from '@angular/cdk/portal';
import {AnyComponent, ComponentInputValues} from '@aviary/store/common/common.types';
import {retryUntil} from '@aviary/store/common/timing.helpers';
import {retryUntilStylesAreLoaded} from '@aviary/store/common/style.helpers';

@Injectable({providedIn: 'root'})
export class PrintService {

  private readonly applicationRef: ApplicationRef = inject(ApplicationRef);
  private readonly injector: Injector = inject(Injector);

  /** Creates an iframe offscreen, renders the specified component inside the iframe's body element, prints the iframe's window, and removes the iframe. */
  public print<C extends AnyComponent>(component: C, inputValues: ComponentInputValues = {}): void {
    const iframeElement: HTMLIFrameElement = this.createIframeElement();

    this.prepareContentWindowInside(
      iframeElement,
      iframeWindow => {
        const portalOutlet: PortalOutlet = this.preparePortalOutletInside(iframeElement);
        this.renderComponentInside(portalOutlet, component, inputValues);

        this.ensureImagesAreLoadedTo(
          iframeWindow,
          () => {
            iframeWindow.onafterprint = () => this.disposeOf(portalOutlet, iframeElement);
            iframeWindow.print();
          }
        );
      }
    );
  }

  private createIframeElement(): HTMLIFrameElement {
    const iframeElement: HTMLIFrameElement = document.createElement('iframe');
    iframeElement.className = 'absolute -top-[1000rem] -left-[1000rem]';
    document.body.appendChild(iframeElement);

    return iframeElement;
  }

  private preparePortalOutletInside(iframeElement: HTMLIFrameElement): PortalOutlet {
    const iframeBodyElement: HTMLElement | undefined = iframeElement.contentDocument?.body;
    if (!iframeBodyElement) throw new Error('Expected locatable <body> within <iframe> created for printing.');

    iframeBodyElement.className = 'iframe-print-content';

    return new DomPortalOutlet(iframeBodyElement, this.applicationRef, this.injector);
  }

  private renderComponentInside<C extends AnyComponent>(portalOutlet: PortalOutlet, component: C, inputValues: ComponentInputValues): void {
    const portal: ComponentPortal<C> = new ComponentPortal<C>(component);
    const componentRef: ComponentRef<C> = portalOutlet.attach(portal);

    componentRef.changeDetectorRef.detach(); // Disable standard change detection on the component so we can activate it when we're ready.

    Object.entries(inputValues).forEach(([inputName, value]) =>
      componentRef.setInput(inputName, value)
    );

    componentRef.changeDetectorRef.detectChanges(); // Trigger change detection now that we know our inputs are set.
  }

  private prepareContentWindowInside(iframeElement: HTMLIFrameElement, onWindowReady: (iframeWindow: Window) => void): void {
    const iframeWindow: Window | null = iframeElement.contentWindow;
    if (!iframeWindow) throw new Error('Expected locatable content window within <iframe> created for printing.');

    // Checking for readyState === 'complete' seems to be the only reliable indicator, especially for Firefox.  (iframe "onload" is known to emit prematurely.)
    retryUntil(
      () => iframeWindow.document.readyState === 'complete',
      () => this.copyMainWindowStylesTo(iframeWindow, onWindowReady),
      () => {
        throw new Error('Timed out waiting for <iframe> document to become ready.')
      }
    );
  }

  private copyMainWindowStylesTo(iframeWindow: Window, onStylesReady: (iframeWindow: Window) => void): void {
    // Copy all <style> elements.
    document.querySelectorAll('style').forEach(styleElement =>
      this.attachCloneOf(styleElement, iframeWindow)
    );

    // Copy all <link> elements that pertain to stylesheets.
    document.querySelectorAll('link').forEach(linkElement => {
      if (linkElement.rel === 'stylesheet') this.attachCloneOf(linkElement, iframeWindow);
    });

    retryUntilStylesAreLoaded(
      () => onStylesReady(iframeWindow),
      'Timed out waiting for stylesheets to be loaded and applied to <iframe>.',
      iframeWindow
    );
  }

  private ensureImagesAreLoadedTo(iframeWindow: Window, onImagesReady: () => void): void {
    retryUntil(
      () =>
        // Ensure the image load is complete, but ignore it if it has an empty src attribute.
        Array.from(iframeWindow.document.body.querySelectorAll('img'))
          .every(imageElement => !imageElement.getAttribute('src') || (imageElement.complete && imageElement.naturalHeight !== 0)
          ),
      onImagesReady,
      () => {
        /* eslint-disable-next-line no-console -- This warning might happen if the organization iconUrl exists but fails, we intend to continue the print but also pop a warning about the issue */
        console.warn('Timed out waiting for images to be loaded in <iframe>. Continuing print operation with possibly unloaded images.');
        onImagesReady();
      }
    );
  }

  private attachCloneOf(element: HTMLElement, targetWindow: Window): void {
    targetWindow.document.head.appendChild(element.cloneNode(true));
  }

  private disposeOf(portalOutlet: PortalOutlet, iframeElement: HTMLIFrameElement): void {
    setTimeout(
      () => {
        portalOutlet.detach();
        portalOutlet.dispose();
        document.body.removeChild(iframeElement);
      },
      2000 // Some browsers freak out when the iframe is deleted during the print.  So wait a couple of seconds before cleanup.  It's user-invisible anyway.
    );
  }
}
